import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSession } from '../../contexts/SessionContext';
import StructuredQuestionForm from '../../components/features/chat/StructuredQuestionForm';
import { ClarificationHint, ClarificationQuestion } from '../../components/features/chat/types';

/**
 * Dev-only perf harness (route /dev/diagram-harness, NODE_ENV=development).
 * Mounts the REAL clarification form + fullscreen diagram on a synthetic
 * catalog sheet so the open path can be measured in a browser without a
 * backend. Query params: callouts (default 80), options (default 6),
 * hintMs (fake hint latency, default 2000), answered=1 (render the answered card
 * and open the map from "View on diagram"), stream=<ms> (re-render the parent
 * every <ms> with fresh callback identities — what the chat page does on every
 * SSE event while a turn streams). The diagram is seeded into the
 * section-diagram sessionStorage cache, so chatService never hits the API.
 */
const SESSION = 'harness';
const IMG_W = 3100;
const IMG_H = 2200;

function buildFixture(nCallouts: number, nOptions: number) {
  const cols = Math.ceil(Math.sqrt(nCallouts * IMG_W / IMG_H));
  const cellW = IMG_W / cols;
  const cellH = IMG_H / Math.ceil(nCallouts / cols);
  const callouts = Array.from({ length: nCallouts }, (_, i) => {
    const col = i % cols, row = Math.floor(i / cols);
    // Every 7th callout sits on top of its neighbour: exercises the overlap solver.
    const jitter = i % 7 === 6 ? -cellW * 0.55 : 0;
    return { label: String(i + 1), bounds: { x: col * cellW + cellW * 0.4 + jitter, y: row * cellH + cellH * 0.4, w: 60, h: 40 }, normalized: false };
  });
  const parts = callouts.map((c, i) => ({
    partId: `P${i + 1}`, callout: c.label, partName: `Bracket assembly ${i + 1}`, part_number: `OE-${1000 + i}`,
    extra_details: i % 2 ? { Side: 'L' } : { Side: 'R' },
  }));
  const canvas = document.createElement('canvas');
  canvas.width = IMG_W; canvas.height = IMG_H;
  const ctx = canvas.getContext('2d')!;
  ctx.fillStyle = '#fff'; ctx.fillRect(0, 0, IMG_W, IMG_H);
  ctx.strokeStyle = '#888'; ctx.lineWidth = 2;
  for (let i = 0; i < 400; i += 1) { ctx.beginPath(); ctx.moveTo(Math.random() * IMG_W, Math.random() * IMG_H); ctx.lineTo(Math.random() * IMG_W, Math.random() * IMG_H); ctx.stroke(); }
  ctx.fillStyle = '#000'; ctx.font = '28px sans-serif';
  callouts.forEach(c => ctx.fillText(c.label, c.bounds.x + 10, c.bounds.y + 30));
  // Split the callouts across two sheets so the harness exercises the real
  // multi-sheet path: tabs, edge arrows, arrow keys, the "Sheet N" chip on a
  // group drawn elsewhere, and opening on the sheet holding the top match.
  // Mirrors section 32/335, where the two control-arm families sit on
  // different sheets.
  const half = Math.ceil(callouts.length / 2);
  const sheet1Labels = callouts.slice(0, half).map(c => c.label);
  const sheet2Labels = callouts.slice(half).map(c => c.label);
  const secondUrl = (() => {
    const c2 = document.createElement('canvas');
    c2.width = IMG_W; c2.height = IMG_H;
    const x = c2.getContext('2d')!;
    x.fillStyle = '#fff'; x.fillRect(0, 0, IMG_W, IMG_H);
    x.strokeStyle = '#aaa'; x.lineWidth = 3;
    for (let i = 0; i < 200; i += 1) { x.beginPath(); x.arc(Math.random() * IMG_W, Math.random() * IMG_H, Math.random() * 120, 0, Math.PI * 2); x.stroke(); }
    x.fillStyle = '#000'; x.font = '28px sans-serif';
    callouts.slice(half).forEach(c => x.fillText(c.label, c.bounds.x + 10, c.bounds.y + 30));
    return c2.toDataURL('image/png');
  })();
  const sheets = [
    { index: 1, id: 'drawing_HARNESS.SHEET.1', code: 'HARNESS.SHEET.1', diagram_url: canvas.toDataURL('image/png'),
      callouts: callouts.slice(0, half), part_callouts: sheet1Labels },
    { index: 2, id: 'drawing_HARNESS.SHEET.2', code: 'HARNESS.SHEET.2', diagram_url: secondUrl,
      callouts: callouts.slice(half), part_callouts: sheet2Labels },
  ];
  const partsWithSheets = parts.map(p => ({ ...p, sheet: sheet1Labels.includes(p.callout) ? 1 : 2 }));
  const diagram = {
    status: 'success', group_id: 'g', section_id: 's',
    section_name: 'CENTRAL BEARING, REAR AXLE CONTROL ARM', group_name: 'SPRINGS AND SUSPENSION',
    diagram_url: canvas.toDataURL('image/png'), callouts, parts: partsWithSheets,
    sheets, sheet_count: sheets.length, primary_sheet: 1,
  };
  sessionStorage.setItem(`catax:section-diagram:v2:${SESSION}:g:s:P1`, JSON.stringify({ cachedAt: Date.now(), value: diagram }));
  const question: ClarificationQuestion = {
    id: 'q1', part_name: 'Bracket', part_label: 'Bracket assembly', question_type: 'single_select',
    text: 'Which bracket assembly do you need?',
    options: parts.slice(0, nOptions).map((p, i) => ({
      id: `opt${i + 1}`, label: p.partName, probability_score: 0.9 - i * 0.1, extra_details: p.extra_details,
      load: [{ group_id: 'g', section_id: 's', partId: p.partId, callout: p.callout, part_number: p.part_number }],
    })),
  };
  return question;
}

const DiagramHarnessPage: React.FC = () => {
  const { t } = useTranslation();
  const { activeSessionId, setActiveSessionId } = useSession();
  const params = new URLSearchParams(window.location.search);
  const nCallouts = Number(params.get('callouts') || 80);
  const nOptions = Number(params.get('options') || 6);
  const hintMs = Number(params.get('hintMs') || 2000);
  const answered = params.get('answered') === '1';
  const stream = Number(params.get('stream') || 0);
  const [tick, setTick] = useState(0);
  useEffect(() => {
    if (!stream) return;
    const id = window.setInterval(() => setTick(t => t + 1), stream);
    return () => window.clearInterval(id);
  }, [stream]);
  const [question, setQuestion] = useState<ClarificationQuestion | null>(null);
  useEffect(() => { setActiveSessionId(SESSION); setQuestion(buildFixture(nCallouts, nOptions)); }, [nCallouts, nOptions, setActiveSessionId]);
  const onRequestHint = useMemo(() => (hintMs < 0 ? undefined : async (q: ClarificationQuestion): Promise<ClarificationHint> => {
    await new Promise(r => setTimeout(r, hintMs));
    return {
      hint: '', intro: 'Harness hint', generated: true, suggested_option_id: null, factors: [],
      candidates: q.options.map(o => ({ option_id: o.id, tags: ['Left', 'Rear'], summary: `Pick ${o.label} when…` })),
    } as ClarificationHint;
  }), [hintMs]);
  if (!question || activeSessionId !== SESSION) return null;
  return (
    <div style={{ maxWidth: 720, margin: '24px auto', padding: 16 }} data-testid="harness-ready" data-tick={tick}>
      <StructuredQuestionForm
        questions={[question]}
        t={t as any}
        // Inline arrow on purpose: a new identity per parent render, like ChatPage.
        onSubmit={() => { void tick; }}
        onRequestHint={onRequestHint}
        initialAnswers={answered ? { q1: { selected_option_ids: ['opt1'] } } : undefined}
      />
    </div>
  );
};

export default DiagramHarnessPage;
